/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * Arquivo: src/routes/router.tsx
 * ---------------------------------------------------------------------------
 * Rotas do app (Desktop + Mobile).
 *
 * Ajustes principais:
 * - SmartHome decide a “landing” pós-login. Zebra/PWA/tela pequena → /m/home;
 *   Desktop → /dashboard.
 * - Suporte a flags rápidas:  /?m=1 força mobile, /?pc=1 força desktop.
 * - Inclui rota do módulo YMS (Yard Management System) em /yard.
 * - Warehouse 3D é carregado em lazy loading para não derrubar o app
 *   caso a cena 3D tenha algum erro.
 * - Inclui rotas do módulo de gestão de pessoal (Workforce):
 *   modelos de turno, planeamento diário, ausências e acidentes de trabalho.
 * - Inclui rotas iniciais do módulo Assembly (rastreio de unidades seriadas).
 * - Inclui rotas do módulo Marketing (campanhas e produtos de campanha).
 * ========================================================================== */

import {
  createBrowserRouter,
  Navigate,
  useParams,
  useNavigate,
} from 'react-router-dom'
import { useEffect, lazy, Suspense } from 'react'
import { useAuthStore } from '@/store/authStore'

// Layout base
import RootLayout from '@/components/RootLayout'

// Embed genérico por iframe (ADR-0052) — usado pelo MFC e pelo Catálogo Global
import EmbedFrame from '@/pages/embed/EmbedFrame'
// MFC embebido por iframe (ADR-0052 F3) — adaptador de slug sobre o EmbedFrame
import MfcFrame from '@/pages/mfc/MfcFrame'

// ======================= Páginas — Operação / Sistema =======================
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import InventoryScan from '@/pages/InventoryScan'
import StockSearch from '@/pages/StockSearch'
import CatalogViewer from '@/pages/CatalogViewer'
import PrintLabels from '@/pages/PrintLabels'
import Printers from '@/pages/printing/Printers'
import Receiving from '@/pages/Receiving'
import Shipping from '@/pages/Shipping'
import WorkforceTasks from '@/pages/WorkforceTasks'
import Settings from '@/pages/Settings'

// -------- Orders (Sales) --------
import OrdersList from '@/pages/orders/OrdersList'
import OrderDetail from '@/pages/orders/OrderDetail'

// ========================== Cadastros (CRUDs) ===============================
import UOMs from '@/pages/catalog/UOMs'
import Products from '@/pages/catalog/Products'
import ProductForm from '@/pages/catalog/ProductForm'
import ProductPacks from '@/pages/catalog/ProductPacks'
import ShelfLifePolicies from '@/pages/catalog/ShelfLifePolicies'
import Locations from '@/pages/core/Locations'
import Companies from '@/pages/core/Companies'
import Sites from '@/pages/core/Sites'
import Warehouses from '@/pages/core/Warehouses'
import Customers from '@/pages/partners/Customers'
import Suppliers from '@/pages/partners/Suppliers'
import SupplierForm from '@/pages/partners/SupplierForm'
import ShelfLifeContractsRoute from '@/pages/partners/ShelfLifeContractsTab'
import Carriers from '@/pages/partners/Carriers'
import SupplierIssues from '@/pages/partners/SupplierIssues'

// =========================== Workforce (admin) ==============================
import WorkforceSkills from '@/pages/workforce/Skills'
import WorkforceDashboard from '@/pages/workforce/Dashboard'
import OperatorSessions from '@/pages/workforce/OperatorSessions'
import OperatorSkillsPage from '@/pages/workforce/OperatorSkillsPage'
import OperatorPerformancePage from '@/pages/workforce/OperatorPerformancePage'
import WorkforceUsers from '@/pages/workforce/Users'
import WorkforceParkingSpots from '@/pages/workforce/ParkingSpots'
import WorkforceEquipments from '@/pages/workforce/Equipments'
import WorkforceDevices from '@/pages/workforce/Devices'

// --- Workforce • Gestão de turnos ---
import ShiftTemplatesPage from '@/pages/workforce/ShiftTemplatesPage'
import ShiftAssignmentsPage from '@/pages/workforce/ShiftAssignmentsPage'
import ShiftZoneTargetsPage from '@/pages/workforce/ShiftZoneTargetsPage'

// --- Workforce • Ausências / Férias ---
import AbsencesPage from '@/pages/workforce/AbsencesPage'
import RejectionReasons from '@/pages/workforce/RejectionReasons'
import RiskTypes from '@/pages/workforce/RiskTypes'

// ==================== Manufacturing / Copack (Desktop) ======================
import CopackOrderNew from '@/pages/manufacturing/CopackOrderNew'

// ========================= Mobile (coletor) =================================
import MobileApp from '@/mobile/MobileApp'

// ===== Utilitários =====
import ScanTest from '@/pages/ScanTest'

/* ===== Warehouse 3D =====
 * Lazy loading: o bundle da cena 3D só é carregado quando o utilizador
 * navega para /warehouse/3d. Se der erro lá dentro, não bloqueia /login
 * nem o resto da aplicação.
 */
const Warehouse3D = lazy(() => import('@/pages/warehouse/Warehouse3D'))

/* ======= Pricing (Frete) ======= */
import RateTablesPage from '@/pages/shipping/RateTables'
import QuoteSimulator from '@/pages/shipping/QuoteSimulator'
import SurchargeTable from '@/pages/shipping/SurchargeTable'

/* ======= MES (Manufacturing Execution System) ======= */
import OrdersPage from '@/pages/mes/OrdersPage'
import WorkCentersPage from '@/pages/mes/WorkCentersPage'
import TerminalPage from '@/pages/mes/TerminalPage'

/* ======= YMS (Yard Management System) =======
 * Painel principal do parque/docas. Primeira versão só lê dados mock,
 * mas a rota já está pronta para futuramente consumir /api/yms/...
 */
import YardDashboard from '@/pages/yard/YardDashboard'

/* ======= Assembly (rastreio de unidades seriadas) =======
 * - AssetsPage: consulta de unidades seriadas, estados e instalação.
 * - AssemblyLinksPage: árvore/histórico de ligações entre componentes.
 */
import AssetsPage from '@/pages/assembly/AssetsPage'
import AssemblyLinksPage from '@/pages/assembly/AssemblyLinksPage'

/* ======= Marketing (campanhas e produtos de campanha) =======
 * Usa as páginas já existentes em src/pages/marketing:
 *  - CampaignsPage.tsx
 *  - CampaignProductsPage.tsx
 */
import MarketingCampaignsPage from '@/pages/marketing/CampaignsPage'
import MarketingCampaignProductsPage from '@/pages/marketing/CampaignProductsPage'

/* ======= Movatic DEV (catalogo + gatilhos de demo/teste — ADR-0042) ======= */
import MovaticDevIndex from '@/pages/movatic-dev/MovaticDevIndex'
import SaleBatchGenerator from '@/pages/movatic-dev/SaleBatchGenerator'
import CatalogoEcra from '@/pages/movatic-dev/CatalogoEcra'

/* ----------------------------------------------------------------------------
 * Helpers de rota
 * ------------------------------------------------------------------------- */

// Decide se devemos abrir a versão mobile (Zebra/PWA/tela pequena/forçado).
function preferMobile(): boolean {
  const qs = new URLSearchParams(location.search)
  if (qs.get('pc') === '1') return false // força desktop
  if (qs.get('m') === '1') return true // força mobile

  // PWA instalada (standalone) → usar mobile
  const standalone =
    (window.matchMedia &&
      window.matchMedia('(display-mode: standalone)').matches) ||
    (navigator as any).standalone === true ||
    document.referrer.startsWith('android-app://')

  const ua = (navigator.userAgent || '').toLowerCase()

  // Reconhece família Zebra/Enterprise/MC/TC/WT/ET e termos comuns
  const isZebra =
    /(zebra|enterprisebrowser|datawedge|mc\d{2,}|tc\d{2,}|wt\d{2,}|et\d{2,}|emkit)/.test(
      ua,
    )

  const isAndroid = /android/.test(ua)
  // Telas até 900px (modo retrato) tratadas como “mobile”
  const smallScreen = Math.min(window.screen.width, window.screen.height) <= 900

  const remembered = localStorage.getItem('movatic.prefer_mobile') === '1'

  return standalone || isZebra || (isAndroid && smallScreen) || remembered
}

// “Home inteligente”: escolhe Desktop ou Mobile e grava preferência no primeiro uso
function SmartHome() {
  const nav = useNavigate()
  useEffect(() => {
    const goMobile = preferMobile()
    if (goMobile) localStorage.setItem('movatic.prefer_mobile', '1')
    nav(goMobile ? '/m/home' : '/dashboard', { replace: true })
  }, [nav])
  return null
}

/* Wrapper para ler :tableId e repassar ao componente SurchargeTable */
function SurchargeTableRoute() {
  const { tableId } = useParams()
  const id = Number(tableId ?? NaN)
  if (!Number.isFinite(id) || id <= 0) {
    return <Navigate to="/pricing/rate-tables" replace />
  }
  return <SurchargeTable tableId={id} />
}

/* -----------------------------------------------------------------------------
// Placeholders leves (mantidos apenas onde ainda não há tela)
----------------------------------------------------------------------------- */
function Placeholder({ title, note }: { title: string; note?: string }) {
  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="opacity-70 mt-2">
        {note ??
          'Tela placeholder. Substitua por uma página real em src/pages/... e atualize o router.'}
      </p>
    </div>
  )
}

function OrdersLinesPage() {
  return (
    <Placeholder
      title="Orders • Linhas de pedido"
      note="Em breve: tabela com filtros (company, status, produto), paginação e acesso ao detalhe do pedido."
    />
  )
}

function PurchasingOrdersPage() {
  return (
    <Placeholder
      title="Compras (PO) • Pedidos de compra"
      note="Lista de POs importadas do ERP com previsão de entrega, fornecedor, status e saldo a receber."
    />
  )
}

function PurchasingLinesPage() {
  return (
    <Placeholder
      title="Compras (PO) • Linhas de compra"
      note="Visão tabular por linhas: saldo, prioridade (ponto de pedido/FIFO), previsões e vínculo com recebimentos."
    />
  )
}

function PurchasingQueuePage() {
  return (
    <Placeholder
      title="Compras (PO) • Fila de recebimento"
      note="Prioriza por ponto de pedido, FIFO e prioridade manual do supervisor."
    />
  )
}

/* ---- Workforce: placeholder apenas para acidentes de trabalho -------------- */
function WFWorkAccidentsPage() {
  return (
    <Placeholder
      title="Workforce • Acidentes de trabalho"
      note="Em breve: registo de ocorrências, severidade, localização e vínculo a ausências por acidente."
    />
  )
}

/** Lê “existe token?” sem depender do tipo do store. */
function hasAnyTokenOutsideReact(): boolean {
  try {
    const st = (useAuthStore as any)?.getState?.()
    if (st?.accessToken) return true
  } catch {
    /* ignore */
  }
  for (const k of ['movatic_token', 'access', 'token']) {
    if (localStorage.getItem(k)) return true
  }
  return false
}

/** Guard de rota privada. */
function Private({ children }: { children: JSX.Element }) {
  const SKIP = (import.meta.env as any)?.VITE_SKIP_AUTH_GUARD === '1'
  // Fix M2.3: hook chamado SEMPRE antes de qualquer early return (rules-of-hooks)
  const hasStoreToken = useAuthStore((s) => !!s.accessToken)
  if (SKIP) return children
  const isAuth = hasStoreToken || hasAnyTokenOutsideReact()
  return isAuth ? children : <Navigate to="/login" replace />
}

/* ----------------------------------------------------------------------------
 * Mapa de rotas
 * ------------------------------------------------------------------------- */
export const router = createBrowserRouter([
  // ---------- Público ----------
  { path: '/login', element: <Login /> },

  // ---------- App Desktop ----------
  {
    path: '/',
    element: (
      <Private>
        <RootLayout />
      </Private>
    ),
    children: [
      // Home inteligente → decide Mobile ou Desktop
      { index: true, element: <SmartHome /> },

      // Acesso direto ao Dashboard desktop
      { path: 'dashboard', element: <Dashboard /> },

      // --------- Operação ---------
      { path: 'inventory/scan', element: <InventoryScan /> },
      { path: 'inventory/search', element: <StockSearch /> },
      { path: 'printing', element: <PrintLabels /> },
      { path: 'printing/printers', element: <Printers /> },
      // ADR-0034 F4b-3: execução guiada (superuser/dev; sem entrada no menu)
      { path: 'catalogo', element: <CatalogViewer /> },
      { path: 'receiving', element: <Receiving /> },
      { path: 'shipping', element: <Shipping /> },
      { path: 'workforce/tasks', element: <WorkforceTasks /> },

      // --------- YMS (Yard Management) ---------
      { path: 'yard', element: <YardDashboard /> },

      // Utilitário
      { path: 'scan-test', element: <ScanTest /> },

      // --------- Workforce (admin) ---------
      { path: 'workforce/dashboard', element: <WorkforceDashboard /> },
      { path: 'workforce/skills', element: <WorkforceSkills /> },
      {
        path: 'workforce/skills/catalog',
        element: <Navigate to="/workforce/skills" replace />,
      },

      // Lista de operadores (usa página de sessões para já)
      { path: 'workforce/operators', element: <OperatorSessions /> },

      // Editor de skills de um operador/usuário
      {
        path: 'workforce/operators/:id/skills',
        element: <OperatorSkillsPage />,
      },
      {
        path: 'workforce/operators/:id/performance',
        element: <OperatorPerformancePage />,
      },
      { path: 'workforce/users', element: <WorkforceUsers /> },
      { path: 'workforce/users/:id/skills', element: <OperatorSkillsPage /> },
      {
        path: 'workforce/operators/skills',
        element: <Navigate to="/workforce/operators" replace />,
      },

      // Parking & Equipamentos
      { path: 'workforce/parking-spots', element: <WorkforceParkingSpots /> },
      { path: 'workforce/equipments', element: <WorkforceEquipments /> },
      { path: 'workforce/devices', element: <WorkforceDevices /> },

      // --- Workforce: turnos ---
      { path: 'workforce/shift-templates', element: <ShiftTemplatesPage /> },
      {
        path: 'workforce/shift-zone-targets',
        element: <ShiftZoneTargetsPage />,
      },
      {
        path: 'workforce/shift-assignments',
        element: <ShiftAssignmentsPage />,
      },

      // --- Workforce: ausências / férias + acidentes ---
      { path: 'workforce/absences', element: <AbsencesPage /> },
      { path: 'workforce/work-accidents', element: <WFWorkAccidentsPage /> },
      { path: 'workforce/rejection-reasons', element: <RejectionReasons /> },
      { path: 'workforce/risk-types', element: <RiskTypes /> },

      // --------- Assembly (rastreio de unidades) ---------
      { path: 'assembly/assets', element: <AssetsPage /> },
      { path: 'assembly/links', element: <AssemblyLinksPage /> },
      { path: 'assembly', element: <Navigate to="/assembly/assets" replace /> },

      // --------- Marketing (campanhas/logística) ----------
      { path: 'marketing/campaigns', element: <MarketingCampaignsPage /> },
      {
        path: 'marketing/campaign-products',
        element: <MarketingCampaignProductsPage />,
      },
      {
        path: 'marketing',
        element: <Navigate to="/marketing/campaigns" replace />,
      },

      // --------- Movatic DEV (demo/teste, gate superuser + sandbox) ----------
      { path: 'movatic-dev', element: <MovaticDevIndex /> },
      { path: 'movatic-dev/criar', element: <SaleBatchGenerator /> },
      { path: 'movatic-dev/catalogo', element: <CatalogoEcra /> },

      // --------- Orders (Sales) ---------
      { path: 'orders/orders', element: <OrdersList /> },
      { path: 'orders/orders/:id', element: <OrderDetail /> },
      { path: 'orders/:id', element: <OrderDetail /> },
      { path: 'orders/lines', element: <OrdersLinesPage /> },
      { path: 'orders', element: <Navigate to="/orders/orders" replace /> },
      {
        path: 'sales/orders',
        element: <Navigate to="/orders/orders" replace />,
      },

      // --------- Purchasing (PO) ---------
      { path: 'purchasing/orders', element: <PurchasingOrdersPage /> },
      { path: 'purchasing/lines', element: <PurchasingLinesPage /> },
      { path: 'purchasing/queue', element: <PurchasingQueuePage /> },

      // --------- Warehouse 3D (lazy) ---------
      {
        path: 'warehouse/3d',
        element: (
          <Suspense
            fallback={
              <div className="p-6 text-sm text-slate-200">
                A carregar visualização 3D do armazém...
              </div>
            }
          >
            <Warehouse3D />
          </Suspense>
        ),
      },

      // --------- Manufacturing / Copack ---------
      {
        path: 'manufacturing/copack-orders',
        element: <Navigate to="/manufacturing/copack-orders/new" replace />,
      },
      { path: 'manufacturing/copack-orders/new', element: <CopackOrderNew /> },
      {
        path: 'manufacturing/copackorder/add',
        element: <Navigate to="/manufacturing/copack-orders/new" replace />,
      },
      {
        path: 'manufacturing/copackorders/new',
        element: <Navigate to="/manufacturing/copack-orders/new" replace />,
      },

      // --------- MES ---------
      { path: 'mes', element: <Navigate to="/mes/orders" replace /> },
      { path: 'mes/orders', element: <OrdersPage /> },
      { path: 'mes/workcenters', element: <WorkCentersPage /> },
      { path: 'mes/terminal', element: <TerminalPage /> },

      // --------- MFC (embebido por iframe, ADR-0052 F3) ---------
      { path: 'mfc', element: <Navigate to="/mfc/dashboard" replace /> },
      { path: 'mfc/dashboard', element: <MfcFrame slug="dashboard" /> },
      { path: 'mfc/miniload', element: <MfcFrame slug="miniload" /> },
      { path: 'mfc/scada', element: <MfcFrame slug="scada" /> },
      { path: 'mfc/missions', element: <MfcFrame slug="missions" /> },
      { path: 'mfc/equipment', element: <MfcFrame slug="equipment" /> },
      { path: 'mfc/topology', element: <MfcFrame slug="topology" /> },
      { path: 'mfc/events', element: <MfcFrame slug="events" /> },
      { path: 'mfc/adapters', element: <MfcFrame slug="adapters" /> },
      { path: 'mfc/rest-api', element: <MfcFrame slug="rest-api" /> },
      { path: 'mfc/opc-ua', element: <MfcFrame slug="opc-ua" /> },
      { path: 'mfc/vda5050', element: <MfcFrame slug="vda5050" /> },
      { path: 'mfc/plc-tags', element: <MfcFrame slug="plc-tags" /> },
      { path: 'mfc/snapshot', element: <MfcFrame slug="snapshot" /> },
      { path: 'mfc/replay', element: <MfcFrame slug="replay" /> },
      { path: 'mfc/wms-mes', element: <MfcFrame slug="wms-mes" /> },
      { path: 'mfc/persistence', element: <MfcFrame slug="persistence" /> },
      { path: 'mfc/scenarios', element: <MfcFrame slug="scenarios" /> },
      {
        path: 'mfc/fault-injection',
        element: <MfcFrame slug="fault-injection" />,
      },
      { path: 'mfc/health', element: <MfcFrame slug="health" /> },
      { path: 'mfc/api-v1', element: <MfcFrame slug="api-v1" /> },

      // --------- Catálogo Global (embebido por iframe, ADR-0052) ---------
      {
        path: 'catalog',
        element: (
          <EmbedFrame
            src="https://catalog.movaticwms.com/?embed=1"
            title="Catálogo Global"
          />
        ),
      },

      // --------- Hub (observabilidade embebida por iframe, ADR-0010) ---------
      {
        path: 'hub',
        element: (
          <EmbedFrame src="https://hub.movaticwms.com/?embed=1" title="Hub" />
        ),
      },

      // --------- MPC / Gestão de Projetos (embebida por iframe, ADR-0052) ---------
      {
        path: 'mpc',
        element: (
          <EmbedFrame
            src="https://mpc.movaticwms.com/?embed=1"
            title="Gestão de Projetos"
          />
        ),
      },

      // --------- Sistema / Cadastros ---------
      { path: 'settings', element: <Settings /> },
      { path: 'catalog/uoms', element: <UOMs /> },
      { path: 'catalog/products', element: <Products /> },
      { path: 'catalog/products/:id/edit', element: <ProductForm /> },
      { path: 'catalog/product-packs', element: <ProductPacks /> },
      {
        path: 'catalog/packs',
        element: <Navigate to="/catalog/product-packs" replace />,
      },
      { path: 'catalog/shelf-life-policies', element: <ShelfLifePolicies /> },
      { path: 'core/locations', element: <Locations /> },
      { path: 'core/companies', element: <Companies /> },
      { path: 'core/sites', element: <Sites /> },
      { path: 'core/warehouses', element: <Warehouses /> },

      // --------- Partners ---------
      { path: 'partners/customers', element: <Customers /> },
      { path: 'partners/suppliers', element: <Suppliers /> },
      { path: 'partners/supplier-issues', element: <SupplierIssues /> },
      {
        path: 'partners/issues',
        element: <Navigate to="/partners/supplier-issues" replace />,
      },
      { path: 'partners/suppliers/new', element: <SupplierForm /> },
      { path: 'partners/suppliers/:id/edit', element: <SupplierForm /> },
      {
        path: 'partners/customers/:id/shelf-life-contracts',
        element: <ShelfLifeContractsRoute partyType="CUSTOMER" />,
      },
      {
        path: 'partners/suppliers/:id/shelf-life-contracts',
        element: <ShelfLifeContractsRoute partyType="SUPPLIER" />,
      },
      { path: 'partners/carriers', element: <Carriers /> },

      /* --------- Pricing (Frete) --------- */
      { path: 'pricing/rate-tables', element: <RateTablesPage /> },
      { path: 'pricing/quote', element: <QuoteSimulator /> },

      // Surcharges com parâmetro obrigatório :tableId
      { path: 'pricing/surcharges/:tableId', element: <SurchargeTableRoute /> },
      // sem parâmetro → leva o usuário para escolher a tabela
      {
        path: 'pricing/surcharges',
        element: <Navigate to="/pricing/rate-tables" replace />,
      },

      // atalhos/compat
      {
        path: 'pricing',
        element: <Navigate to="/pricing/rate-tables" replace />,
      },
      {
        path: 'shipping/pricing',
        element: <Navigate to="/pricing/rate-tables" replace />,
      },
      {
        path: 'shipping/pricing/tables',
        element: <Navigate to="/pricing/rate-tables" replace />,
      },
      {
        path: 'shipping/pricing/quote',
        element: <Navigate to="/pricing/quote" replace />,
      },
      {
        path: 'shipping/pricing/surcharges/:tableId',
        element: <SurchargeTableRoute />,
      },
      {
        path: 'shipping/pricing/surcharges',
        element: <Navigate to="/pricing/rate-tables" replace />,
      },
    ],
  },

  // ---------- App Mobile (coletor) ----------
  {
    path: '/m/*',
    element: (
      <Private>
        <MobileApp />
      </Private>
    ),
  },

  // ---------- Fallback ----------
  { path: '*', element: <Navigate to="/" replace /> },
])
